import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import ProfileHero from './components/ProfileHero';
import ProfileTabs from './components/ProfileTabs';
import BookingPanel from './components/BookingPanel';
import AIChatAssistant from './components/AIChatAssistant';
import { professionalsService, bookingsService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';

const ProfessionalProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const profileId = searchParams?.get('id');
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showBooking, setShowBooking] = useState(false);

  useEffect(() => {
    if (profileId) {
      loadProfessional();
    } else {
      setLoading(false);
    }
  }, [profileId]);

  const loadProfessional = async () => {
    setLoading(true);
    const { data, error } = await professionalsService?.getById(profileId);
    if (data) {
      // Normalize to expected shape
      setProfessional({
        id: data?.id,
        category: data?.category,
        categoryLabel: data?.category ? (data?.category?.charAt(0)?.toUpperCase() + data?.category?.slice(1)) : '',
        name: data?.user?.full_name || data?.company || 'Professional',
        company: data?.company || '',
        location: data?.location || '',
        rating: parseFloat(data?.rating) || 0,
        reviewCount: data?.review_count || 0,
        verified: data?.verified || false,
        startingPrice: data?.starting_price || '',
        specializations: data?.specializations || [],
        bio: data?.bio || '',
        phone: data?.phone || '',
        email: data?.user?.email || '',
        responseTime: data?.response_time || '',
        completedJobs: data?.completed_jobs || 0,
        repeatClients: data?.repeat_clients || '0%',
        certifications: data?.certifications || [],
        portfolio: (data?.portfolio || [])?.map(item => ({ url: item?.image_url, caption: item?.caption })),
        availability: data?.availability || '',
        services: (data?.services || [])?.map(s => ({ name: s?.name, price: s?.price, description: s?.description })),
        reviews: (data?.reviews || [])?.map(r => ({
          author: r?.author_name,
          rating: r?.rating,
          date: r?.review_date,
          text: r?.review_text
        }))
      });
    }
    setLoading(false);
  };

  const handleBookingSubmit = async (bookingData) => {
    const { data, error } = await bookingsService?.create({
      userId: user?.id || null,
      proId: profileId,
      serviceName: bookingData?.service || '',
      date: bookingData?.date || null,
      time: bookingData?.time || '',
      notes: bookingData?.notes || '',
      name: bookingData?.name || '',
      email: bookingData?.email || '',
      phone: bookingData?.phone || ''
    });
    return { data, error };
  };

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) =>
      <span key={i} style={{ color: i < Math.floor(rating) ? '#FFD700' : 'rgba(255,215,0,0.3)', fontSize: '14px' }}>★</span>
    );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050A14' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#00D4FF' }} />
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050A14' }}>
        <div className="text-center">
          <Icon name="UserX" size={48} color="rgba(0,212,255,0.3)" className="mx-auto mb-4" />
          <p className="text-white font-semibold mb-2">Professional not found</p>
          <button onClick={() => navigate('/professional-marketplace')} className="text-sm" style={{ color: '#00D4FF' }}>Browse all professionals</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>{professional?.name} - {professional?.categoryLabel} | ListingLens</title></Helmet>
      <div className="min-h-screen" style={{ background: '#050A14' }}>
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(0,212,255,0.05) 0%, transparent 50%)', }} />
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>

        <nav className="sticky top-0 z-40 px-4 py-3" style={{ background: 'rgba(5,10,20,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/professional-marketplace')} className="flex items-center gap-2 text-sm" style={{ color: '#6B9EC4' }}>
              <Icon name="ArrowLeft" size={16} color="currentColor" />
              Back to Professionals
            </button>
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <img src="/assets/images/logo_LL-1772022913346.jpeg" alt="ListingLens logo" className="h-8 w-auto rounded-lg" style={{ border: '1px solid rgba(0,212,255,0.4)', maxWidth: '100px' }} />
            </button>
          </div>
        </nav>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <ProfileHero 
                professional={professional} 
                renderStars={renderStars} 
                onBook={() => setShowBooking(true)}
                onContact={() => setShowBooking(true)}
              />
              <ProfileTabs
                professional={professional}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                renderStars={renderStars}
              />
            </div>
            <div className="lg:col-span-1 space-y-6">
              <BookingPanel
                professional={professional}
                show={showBooking}
                onToggle={() => setShowBooking(!showBooking)}
                onSubmit={handleBookingSubmit}
                onClose={() => setShowBooking(false)}
              />
              <AIChatAssistant professional={professional} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfessionalProfile;